import * as React from 'react';

import { Action, Dispatch, bindActionCreators } from 'redux';
import { ActivityItem, Checkbox, IActivityItemProps, Icon, Link, Spinner, TextField } from '@fluentui/react';
import { createNote, deleteNote, fetchNotes, updateNote } from '../../redux/patient-notes/actions';
import { AsyncData } from '../../redux/core';
import BlockUi from 'react-block-ui';
import { OkCancelDialog } from '../core/OkCancelDialog';
import { ROUTES } from '../../config/routes';
import { RootState } from '../../redux/types';
import { connect } from 'react-redux';
import { PatientNote, PatientNoteContent } from '../../api/patients.types';

export interface OwnProps {
  patientId: string;
  visitCode?: string;
  readOnly?: boolean;
}

export interface StateProps {
  notes?: AsyncData<PatientNote[]>;
}

export interface DispatchProps {
  actions: {
    fetchNotes: (patientId: string) => void;
    createNote: (patientId: string, note: string, visitCode?: string, operationsOnly?: boolean) => void;
    deleteNote: (note: PatientNote) => void;
    updateNote: (note: PatientNote) => void;
  };
}

type PatientNotesProps = OwnProps & StateProps & DispatchProps;

export interface PatientNotesState {
  currentNote: string;
  isSaving: boolean;
  hideFlagsDialog: boolean;
  createOperationsOnlyMessage: boolean;
}

export class Component extends React.Component<PatientNotesProps, PatientNotesState> {
  constructor(props: PatientNotesProps) {
    super(props);
    this.state = {
      currentNote: '',
      isSaving: false,
      hideFlagsDialog: true,
      createOperationsOnlyMessage: false,
    };
  }

  public componentDidMount() {
    this.props.actions.fetchNotes(this.props.patientId);
  }

  public componentDidUpdate(prevProps: PatientNotesProps) {
    if (this.props.patientId !== prevProps.patientId) {
      this.props.actions.fetchNotes(this.props.patientId);
      this.setState({ createOperationsOnlyMessage: false });
    }

    if (this.state.isSaving && this.props !== prevProps) {
      this.setState({ isSaving: false, currentNote: '' });
    }
  }

  public render(): JSX.Element | null {
    const { notes } = this.props;
    const activityItems: JSX.Element[] = [];
    if (notes?.data) {
      notes.data.forEach((note, index) => {
        const props = this.generateActivityItemProps(note);
        if (props) {
          activityItems.push(<ActivityItem style={{ marginTop: 10 }} {...props} key={index} />);
        }
      });
    }

    return (
      <div>
        {!notes?.data?.length && <div style={{ marginTop: 5, marginBottom: 5 }}>No patient notes</div>}
        {!this.props.readOnly && (
          <BlockUi tag="div" blocking={this.state.isSaving}>
            <TextField multiline={true} rows={3} placeholder="Add patient note" onKeyPress={this.onNoteKeyPress} onChange={this.onNoteChange} value={this.state.currentNote} />
            <div style={{ marginTop: 5 }}>
              <Checkbox
                label="Only visible to Operations"
                checked={this.state.createOperationsOnlyMessage}
                onChange={(ev, checked) => this.setState({ createOperationsOnlyMessage: checked! })}
              />
            </div>
          </BlockUi>
        )}
        {this.props.notes?.isFetching && <Spinner />}
        {this.props.notes?.data && <div>{activityItems}</div>}
      </div>
    );
  }

  private generateActivityItemProps(patientNote: PatientNote): IActivityItemProps | null {
    const props: IActivityItemProps = {
      activityDescription: [],
      activityIcon: patientNote.operationsOnly ? <Icon iconName={'Hide'} /> : <Icon iconName={'Message'} />,
      comments: [],
      timeStamp: `${new Date(patientNote.createdAt).toLocaleTimeString()} - ${new Date(patientNote.createdAt).toLocaleDateString()}`,
    };
    let activityIndex = 1;

    if (patientNote.visitCode) {
      (props.activityDescription as any[]).push(<span key={activityIndex++}>For visit </span>);
      (props.activityDescription as any[]).push(
        <Link key={activityIndex++} href={`${ROUTES.visits}/${patientNote.visitCode}`}>
          {patientNote.visitCode}{' '}
        </Link>
      );
    }

    try {
      const data: PatientNoteContent = JSON.parse(patientNote.note) as PatientNoteContent;
      switch (data.kind) {
        case 'comment':
          (props.activityDescription as any[]).push(<span key={activityIndex++}>{patientNote.createdByDisplayName} commented</span>);
          (props.comments as any[]).push(<span key={1}>{data.comment}</span>);
          break;
        case 'flag':
          (props.activityDescription as any[]).push(<span key={activityIndex++}>{patientNote.createdByDisplayName} </span>);

          if (data.added.length > 0) {
            (props.activityDescription as any[]).push(<span key={activityIndex++}>added </span>);
            (props.activityDescription as any[]).push(
              <span style={{ color: '#333', fontWeight: 600 }} key={activityIndex++}>
                {data.added.join(', ')}{' '}
              </span>
            );
            if (data.added.length === 1) {
              (props.activityDescription as any[]).push(<span key={activityIndex++}>flag </span>);
            } else {
              (props.activityDescription as any[]).push(<span key={activityIndex++}>flags </span>);
            }
            if (data.removed.length > 0) {
              (props.activityDescription as any[]).push(<span key={activityIndex++}>and </span>);
            }
          }

          if (data.removed.length > 0) {
            (props.activityDescription as any[]).push(<span key={activityIndex++}>removed </span>);
            (props.activityDescription as any[]).push(
              <span style={{ color: '#333', fontWeight: 600 }} key={activityIndex++}>
                {data.removed.join(', ')}{' '}
              </span>
            );
            if (data.removed.length === 1) {
              (props.activityDescription as any[]).push(<span key={activityIndex++}>flag </span>);
            } else {
              (props.activityDescription as any[]).push(<span key={activityIndex++}>flags </span>);
            }
          }

          (props.comments as any[]).push(<span key={1}>{data.comment}</span>);
          break;
        case 'pcp':
          return null;
        default:
          break;
      }
    } catch (e: any) {
      (props.activityDescription as any[]).push(<span key={activityIndex++}>{patientNote.createdByDisplayName} commented</span>);
      (props.comments as any[]).push(<span key={1}>{patientNote.note}</span>);
    }

    props.onRenderTimeStamp = (p?: IActivityItemProps, defaultRender?: (p?: IActivityItemProps) => JSX.Element | null): JSX.Element | null => {
      if (defaultRender) {
        return (
          <span>
            {defaultRender(p)}
            {!this.props.readOnly && (
              <>
                <Link onClick={() => this.onDeleteClick(patientNote)}>delete</Link>
                <span>&nbsp;|&nbsp;</span>
                <Link
                  onClick={() =>
                    this.props.actions!.updateNote({
                      ...patientNote,
                      operationsOnly: !patientNote.operationsOnly,
                    })
                  }
                >
                  toggle visibility
                </Link>
              </>
            )}
          </span>
        );
      }
      return null;
    };

    return props;
  }

  private onDeleteClick = (patientNote: PatientNote) => {
    OkCancelDialog.show({
      title: 'Delete patient note',
      subText: 'This will delete the patient note. Are you sure?',
      onOk: () => this.props.actions!.deleteNote(patientNote),
    });
  };

  private onNoteKeyPress = (e: any) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.addNote();
    }
  };

  private onNoteChange = (evt: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, value?: string) => {
    if (value !== undefined) {
      this.setState({ currentNote: value });
    }
  };

  private addNote() {
    if (this.state.currentNote.length === 0) {
      return;
    }

    this.setState({ isSaving: true });
    const note = JSON.stringify({ kind: 'comment', comment: this.state.currentNote });
    this.props.actions.createNote(this.props.patientId, note, this.props.visitCode, this.state.createOperationsOnlyMessage);
  }
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => {
  return {
    notes: state.patientNotes.data[ownProps.patientId],
  };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>): DispatchProps => {
  return {
    actions: bindActionCreators({ fetchNotes, createNote, deleteNote, updateNote }, dispatch),
  };
};

export const PatientNotes = connect<StateProps, DispatchProps, OwnProps, RootState>(mapStateToProps, mapDispatchToProps)(Component);
